import React, { useState, useEffect } from 'react';
import styles from './EditProfile.module.css';

// 1. Switched prop name from initialData to memberData to match your main component
const EditProfile = ({ isOpen, onClose, memberData, onSave }) => {
  const [formData, setFormData] = useState({
    membershipNo: '',
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    dob: '',
    gender: '',
    maritalStatus: '',
    nin: '',
    bvn: '',
    residentialAddress: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2. Sync memberData into form state whenever the modal opens
  useEffect(() => {
    if (isOpen && memberData) {
      setFormData({
        membershipNo: memberData.member_number || '',
        title: memberData.title || '',
        firstName: memberData.first_name || '',
        middleName: memberData.other_name || '', // Mapping other_name to middleName
        lastName: memberData.last_name || '',
        emailAddress: memberData.email || '',
        phoneNumber: memberData.phone_number || '',
        dob: memberData.dob || '',
        gender: memberData.gender || '',
        maritalStatus: memberData.marital_status || '',
        nin: memberData.nin || '',
        bvn: memberData.bvn || '',
        residentialAddress: memberData.address || '' // Mapping address to residentialAddress
      });
    }
  }, [isOpen, memberData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        first_name: formData.firstName,
        other_name: formData.middleName,
        last_name: formData.lastName,
        email: formData.emailAddress,
        phone_number: formData.phoneNumber,
        dob: formData.dob,
        gender: formData.gender,
        marital_status: formData.maritalStatus,
        nin: formData.nin,
        bvn: formData.bvn,
        address: formData.residentialAddress,
      };

      const saved = await onSave(payload);
      if (saved !== false) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Edit Personal Information</h2>
          <button className={styles.closeButton} onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            
            {/* Row 1 */}
            <div className={styles.formGroup}>
              <label>Membership No <span className={styles.required}>*</span></label>
              <input type="text" name="membershipNo" value={formData.membershipNo} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Title <span className={styles.required}>*</span></label>
              <select name="title" value={formData.title} onChange={handleChange} required>
                <option value="">Select Title</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Miss">Miss</option>
                <option value="Dr">Dr</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>First Name <span className={styles.required}>*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
            </div>

            {/* Row 2 */}
            <div className={styles.formGroup}>
              <label>Middle Name</label>
              <input type="text" name="middleName" value={formData.middleName} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name <span className={styles.required}>*</span></label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Email Address <span className={styles.required}>*</span></label>
              <input type="email" name="emailAddress" value={formData.emailAddress} onChange={handleChange} required />
            </div>

            {/* Row 3 */}
            <div className={styles.formGroup}>
              <label>Phone Number <span className={styles.required}>*</span></label>
              <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Date of Birth</label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Gender</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            {/* Row 4 */}
            <div className={styles.formGroup}>
              <label>Marital Status</label>
              <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                <option value="">Select Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>NIN <span className={styles.required}>*</span></label>
              <input type="text" name="nin" value={formData.nin} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>BVN <span className={styles.required}>*</span></label>
              <input type="text" name="bvn" value={formData.bvn} onChange={handleChange} required />
            </div>

            {/* Row 5 - Full Width */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>Residential Address <span className={styles.required}>*</span></label>
              <textarea name="residentialAddress" value={formData.residentialAddress} onChange={handleChange} rows="4" required />
            </div>

          </div>

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className={styles.saveBtn} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;